import React, { useState, useEffect, useCallback } from 'react';
import { getToken } from '../../services/authService';
import { fetchAllCohorts } from '../../services/cohortService';
import { RefreshCw, Save } from 'lucide-react';

const API_BASE = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace('/auth', '/admin')
  : (process.env.NODE_ENV === 'production' ? '/api/admin' : 'http://localhost:8080/api/admin');

interface CohortRow {
  id: number;
  bootcamp_id: string;
  cohort_name: string;
  cohort_code: string;
  starts_at: string | null;
  is_active: boolean;
  weeks_theoric_unlocked: number;
  weeks_group_unlocked: number;
  enrolled_count: number;
}

export default function CohortManagement() {
  const [cohorts, setCohorts] = useState<CohortRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<number, Partial<CohortRow>>>({});

  const load = useCallback(async () => {
    setLoading(true);
    const token = getToken() || '';
    const data = await fetchAllCohorts(token);
    setCohorts(data);
    setEdits({});
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const edit = (id: number, field: string, value: any) => {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const getVal = (cohort: CohortRow, field: keyof CohortRow) => {
    return edits[cohort.id]?.[field] ?? cohort[field];
  };

  const hasEdits = (id: number) => !!edits[id] && Object.keys(edits[id]).length > 0;

  const save = async (id: number) => {
    if (!edits[id]) return;
    setSaving(id);
    try {
      const token = getToken() || '';
      const res = await fetch(`${API_BASE}/cohorts.php?cohort_id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(edits[id]),
      });
      if (res.ok) {
        showToast('Camada actualizada');
        load();
      } else {
        showToast('Error al guardar');
      }
    } catch {
      showToast('Error de conexion');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Camadas</h1>
          <p className="text-sm text-gray-500 mt-1">Gestiona fechas de inicio y desbloqueo de semanas por camada</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <RefreshCw className="w-4 h-4" /> Recargar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando...</div>
      ) : (
        <div className="space-y-4">
          {cohorts.map(cohort => (
            <div key={cohort.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-100">
                <div>
                  <span className="font-semibold text-gray-900">{cohort.cohort_name}</span>
                  <span className="text-xs text-gray-500 ml-2 bg-gray-200 px-2 py-0.5 rounded">{cohort.cohort_code}</span>
                  <span className="text-xs text-gray-400 ml-3">{cohort.enrolled_count} alumnos</span>
                </div>
                {hasEdits(cohort.id) && (
                  <button
                    onClick={() => save(cohort.id)}
                    disabled={saving === cohort.id}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {saving === cohort.id ? 'Guardando...' : 'Guardar'}
                  </button>
                )}
              </div>

              <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Fecha de inicio</label>
                  <input
                    type="date"
                    value={getVal(cohort, 'starts_at') as string || ''}
                    onChange={e => edit(cohort.id, 'starts_at', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Semana 1 se libera en esta fecha, +7 dias cada semana</p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Semanas teorica desbloqueadas</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={10}
                      value={getVal(cohort, 'weeks_theoric_unlocked') as number}
                      onChange={e => edit(cohort.id, 'weeks_theoric_unlocked', Number(e.target.value))}
                      className="flex-1 accent-emerald-600"
                    />
                    <span className="text-sm font-bold text-gray-700 w-8 text-center">
                      {getVal(cohort, 'weeks_theoric_unlocked') as number}/10
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Semanas grupal desbloqueadas</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={10}
                      value={getVal(cohort, 'weeks_group_unlocked') as number}
                      onChange={e => edit(cohort.id, 'weeks_group_unlocked', Number(e.target.value))}
                      className="flex-1 accent-emerald-600"
                    />
                    <span className="text-sm font-bold text-gray-700 w-8 text-center">
                      {getVal(cohort, 'weeks_group_unlocked') as number}/10
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
